// src/pages/ROICalculatorPage.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";
import { TrendingUp, DollarSign, Users, Tv, Smartphone, Globe, Zap, ArrowRight } from "lucide-react";
import { clsx } from "clsx";

// ── Animated number hook ──────────────────────────────────────
function useAnimatedValue(target: number, duration = 600) {
  const [current, setCurrent] = useState(target);
  const prev = useRef(target);
  const raf  = useRef<number>();
  useEffect(() => {
    const start     = prev.current;
    const diff      = target - start;
    const startTime = performance.now();
    const animate   = (now: number) => {
      const p     = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCurrent(Math.round(start + diff * eased));
      if (p < 1) raf.current = requestAnimationFrame(animate);
      else prev.current = target;
    };
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);
  return current;
}

function fmt(n: number, type: "currency" | "percent" | "number" | "multiplier" = "number"): string {
  if (type === "percent")    return `${n.toFixed(1)}%`;
  if (type === "multiplier") return `${n.toFixed(1)}x`;
  if (type === "currency") {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
  }
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

// ── Slider component ──────────────────────────────────────────
interface SliderProps {
  label:    string;
  sublabel?: string;
  value:    number;
  min:      number;
  max:      number;
  step:     number;
  format:   "currency" | "percent" | "number";
  onChange: (v: number) => void;
}

function Slider({ label, sublabel, value, min, max, step, format, onChange }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-800">{label}</p>
          {sublabel && <p className="text-2xs text-slate mt-0.5">{sublabel}</p>}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-navy tabular bg-navy/5 px-2 py-0.5 rounded-md">
            {fmt(value, format)}
          </span>
        </div>
      </div>
      <div className="relative h-5 flex items-center">
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue to-cyan rounded-full transition-all duration-150"
            style={{ width: `${pct}%` }} />
        </div>
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full opacity-0 cursor-pointer h-full" />
        <div className="absolute w-4 h-4 rounded-full bg-white border-2 border-blue shadow-md pointer-events-none transition-all duration-150"
          style={{ left: `calc(${pct}% - 8px)` }} />
      </div>
      <div className="flex justify-between text-2xs text-slate/60">
        <span>{fmt(min, format)}</span>
        <span>{fmt(max, format)}</span>
      </div>
    </div>
  );
}

// ── Match rate arc gauge ──────────────────────────────────────
function MatchRateGauge({ current, target = 92 }: { current: number; target?: number }) {
  const width  = 180;
  const height = 130;
  const cx     = width / 2;
  const cy     = 88;           // arc spans y=88-62=26 to y=88+31=119 → fits in 130
  const r      = 62;
  const stroke = 12;
  const startAngle = -210;
  const sweepAngle = 240;

  function polarToCartesian(angle: number) {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(from: number, to: number) {
    const s = polarToCartesian(from);
    const e = polarToCartesian(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  }

  const currentAngle = startAngle + (sweepAngle * current) / 100;
  const targetAngle  = startAngle + (sweepAngle * target)  / 100;
  const fullEnd      = startAngle + sweepAngle;

  return (
    <div className="flex flex-col items-center">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Track */}
        <path d={arcPath(startAngle, fullEnd)} fill="none" stroke="#e5e7eb" strokeWidth={stroke} strokeLinecap="round" />
        {/* Current (gray) */}
        <path d={arcPath(startAngle, currentAngle)} fill="none" stroke="#94a3b8" strokeWidth={stroke} strokeLinecap="round" />
        {/* MRN target (blue) */}
        <path d={arcPath(currentAngle, targetAngle)} fill="none" stroke="#3056D3" strokeWidth={stroke + 2} strokeLinecap="round" opacity={0.9} />
        {/* Center labels */}
        <text x={cx} y={cy - 10} textAnchor="middle" fill="#1E2761" fontSize={24} fontWeight={900} fontFamily="Inter, sans-serif">
          {target}%
        </text>
        <text x={cx} y={cy + 8} textAnchor="middle" fill="#64748B" fontSize={9} fontFamily="Inter, sans-serif">
          WITH MRN
        </text>
      </svg>
      <p className="text-xs font-semibold text-emerald-600 mt-1 leading-tight">
        +{(target - current).toFixed(0)}pp match rate lift
      </p>
      <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">
        Today: {current}%
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function ROICalculatorPage() {
  const [annualSpend,    setAnnualSpend]    = useState(5_000_000);
  const [matchRate,      setMatchRate]      = useState(25);
  const [progCostPct,    setProgCostPct]    = useState(30);
  const [loyaltyMembers, setLoyaltyMembers] = useState(1_000_000);
  const [wastedImpr,     setWastedImpr]     = useState(20);

  // ── Calculations ─────────────────────────────────────────────
  const mrnMatchRate    = 92;
  const mrnPlatformFee  = 0.10;

  // DSP savings: 30% reduction on programmatic portion (80% of spend)
  const programmaticSpend   = annualSpend * 0.80;
  const currentProgCost     = programmaticSpend * (progCostPct / 100);
  const mrnProgCost         = programmaticSpend * 0.21;          // MRN direct = ~21%
  const dspSavings          = Math.max(0, currentProgCost - mrnProgCost);

  // Suppression savings: reduce wasted impressions by 70%
  const wastedSpend         = annualSpend * (wastedImpr / 100);
  const suppressionSavings  = wastedSpend * 0.70;

  const totalSavings        = dspSavings + suppressionSavings;
  const mrnFee              = annualSpend * mrnPlatformFee;
  const netSavings          = totalSavings - mrnFee;

  // New revenue opportunity
  const conquestProspects   = loyaltyMembers * 3.5;
  const valuePerPerson      = 4.20;                              // avg media value
  const newRevenueOppty     = conquestProspects * valuePerPerson;

  // ROI multiplier
  const roiMultiplier       = mrnFee > 0 ? (netSavings + newRevenueOppty * 0.15) / mrnFee : 0;

  // 12-month projection data
  const projectionData = Array.from({ length: 12 }, (_, i) => {
    const month       = i + 1;
    const monthSavings = (totalSavings / 12) * month;
    const compounding  = monthSavings * (1 + (month / 12) * 0.15); // data compounding
    return { month: `M${month}`, cumSavings: Math.round(monthSavings), compounding: Math.round(compounding) };
  });

  // Animated values
  const animSavings  = useAnimatedValue(Math.round(totalSavings));
  const animNet      = useAnimatedValue(Math.round(netSavings));
  const animOppty    = useAnimatedValue(Math.round(newRevenueOppty));
  const animROI      = useAnimatedValue(Math.round(roiMultiplier * 10));

  // Bar chart data
  const withoutMRN = [
    { name: "Working Media",      value: Math.round(annualSpend * (1 - progCostPct/100 - wastedImpr/100) * 0.85), fill: "#3056D3" },
    { name: "Intermediary Fees",  value: Math.round(currentProgCost), fill: "#94a3b8" },
    { name: "Wasted Impressions", value: Math.round(wastedSpend),     fill: "#ef4444" },
    { name: "Measurement Gap",    value: Math.round(annualSpend * 0.05), fill: "#fbbf24" },
  ];
  const withMRN = [
    { name: "Working Media",      value: Math.round(annualSpend * (1 - 0.21 - wastedImpr/100 * 0.30) * 0.85), fill: "#3056D3" },
    { name: "MRN Platform Fee",   value: Math.round(mrnFee),          fill: "#0891B2" },
    { name: "DSP Savings",        value: Math.round(dspSavings),      fill: "#10b981" },
    { name: "Suppression Savings",value: Math.round(suppressionSavings), fill: "#059669" },
  ];

  const barData = [
    { name: "Without MRN", ...Object.fromEntries(withoutMRN.map(d => [d.name, d.value])) },
    { name: "With MRN",    ...Object.fromEntries(withMRN.map(d => [d.name, d.value]))    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="page-header mb-0">
        <h1 className="page-title">ROI Calculator</h1>
        <p className="page-subtitle">Show prospects exactly what they're leaving on the table — and what MRN delivers.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* ── LEFT: Input Panel ── */}
        <div className="col-span-4 space-y-4">
          <div className="card border border-navy/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-navy/10 flex items-center justify-center">
                <DollarSign size={14} className="text-navy" />
              </div>
              <h3 className="text-sm font-semibold text-navy">Your Current Media Profile</h3>
            </div>
            <div className="space-y-5">
              <Slider label="Annual Media Spend" value={annualSpend} min={500_000} max={50_000_000}
                step={250_000} format="currency" onChange={setAnnualSpend} />
              <Slider label="Current Identity Match Rate" sublabel="% of your audience you can actually target"
                value={matchRate} min={10} max={50} step={1} format="percent" onChange={setMatchRate} />
              <Slider label="Programmatic Overhead" sublabel="LiveRamp, data onboarding, intermediary fees"
                value={progCostPct} min={15} max={40} step={1} format="percent" onChange={setProgCostPct} />
              <Slider label="Monthly Loyalty Members" value={loyaltyMembers} min={100_000} max={10_000_000}
                step={100_000} format="number" onChange={setLoyaltyMembers} />
              <Slider label="Estimated Wasted Impressions" sublabel="Reaching converters or non-intenders"
                value={wastedImpr} min={10} max={35} step={1} format="percent" onChange={setWastedImpr} />
            </div>
          </div>

          {/* Footer note */}
          <div className="bg-navy/5 border border-navy/10 rounded-lg p-3">
            <p className="text-2xs text-slate leading-relaxed">
              <span className="font-semibold text-navy">Powered by the MRN Super Identity Graph</span> — 498M individuals across 45B+ connected touchpoints. All calculations based on median client outcomes.
            </p>
          </div>
        </div>

        {/* ── RIGHT: Results Dashboard ── */}
        <div className="col-span-8 space-y-4">

          {/* Hero numbers */}
          <div className="grid grid-cols-3 gap-4">
            {/* Match rate gauge */}
            <div className="card text-center border border-blue/10">
              <p className="section-label mb-2">Identity Match Rate</p>
              <MatchRateGauge current={matchRate} target={mrnMatchRate} />
              <p className="text-xs text-slate mt-1">
                MRN resolves <span className="font-semibold text-navy">{mrnMatchRate - matchRate}pp more</span> of your audience
              </p>
            </div>

            {/* Annual savings */}
            <div className="card border border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30 text-center">
              <p className="section-label mb-3">Annual Efficiency Gains</p>
              <p className="text-4xl font-black tabular text-emerald-600 leading-none">
                {fmt(animSavings, "currency")}
              </p>
              <p className="text-xs text-slate mt-2">saved annually</p>
              <div className="mt-3 space-y-1 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-slate">DSP savings (30% lower CPM)</span>
                  <span className="font-semibold text-emerald-600 tabular">{fmt(dspSavings, "currency")}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate">Suppression (70% waste recovery)</span>
                  <span className="font-semibold text-emerald-600 tabular">{fmt(suppressionSavings, "currency")}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-emerald-100 pt-1 mt-1">
                  <span className="text-slate">MRN platform fee (~10%)</span>
                  <span className="font-semibold text-slate tabular">-{fmt(mrnFee, "currency")}</span>
                </div>
                <div className="flex justify-between text-xs font-bold">
                  <span className="text-emerald-700">Net savings</span>
                  <span className="text-emerald-700 tabular">{fmt(animNet, "currency")}</span>
                </div>
              </div>
            </div>

            {/* New revenue opportunity */}
            <div className="card border border-cyan/20 bg-gradient-to-br from-white to-cyan/5 text-center">
              <p className="section-label mb-3">New Revenue Opportunity</p>
              <p className="text-4xl font-black tabular text-cyan leading-none">
                {fmt(animOppty, "currency")}
              </p>
              <p className="text-xs text-slate mt-2">conquest prospect value</p>
              <div className="mt-3 space-y-1 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-slate">Loyalty members</span>
                  <span className="font-semibold text-navy tabular">{fmt(loyaltyMembers)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate">MRN conquest prospects</span>
                  <span className="font-semibold text-cyan tabular">{fmt(conquestProspects)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate">Avg value per person</span>
                  <span className="font-semibold text-navy tabular">$4.20</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-cyan/10">
                <p className="text-xs text-slate">Already researching your categories</p>
              </div>
            </div>
          </div>

          {/* Spend allocation chart */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-1">Where Your Money Goes — Today vs. With MRN</h3>
            <p className="text-xs text-slate mb-3">See how MRN shifts spend from waste to working media</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: "Without MRN", working: withoutMRN[0].value, fees: withoutMRN[1].value, waste: withoutMRN[2].value, gap: withoutMRN[3].value },
                { name: "With MRN",    working: withMRN[0].value,    fee: withMRN[1].value,     dspSave: withMRN[2].value, suppSave: withMRN[3].value },
              ]} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#1E2761", fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                  formatter={(v: number, n: string) => [fmt(v, "currency"), n]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="working"  stackId="a" fill="#3056D3" name="Working Media"       radius={[0,0,0,0]} />
                <Bar dataKey="fees"     stackId="a" fill="#94a3b8" name="Intermediary Fees"   />
                <Bar dataKey="waste"    stackId="a" fill="#ef4444" name="Wasted Impressions"  />
                <Bar dataKey="gap"      stackId="a" fill="#fbbf24" name="Measurement Gap"     radius={[4,4,0,0]} />
                <Bar dataKey="fee"      stackId="b" fill="#0891B2" name="MRN Platform Fee"    />
                <Bar dataKey="dspSave"  stackId="b" fill="#10b981" name="DSP Savings"         />
                <Bar dataKey="suppSave" stackId="b" fill="#059669" name="Suppression Savings" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Channel impact cards */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: Globe,      color: "bg-navy/10 text-navy",  label: "O&O",     stat: "90M+",  sub: "monthly uniques", detail: "Exclusive reach unavailable programmatically" },
              { icon: DollarSign, color: "bg-blue/10 text-blue",  label: "Display", stat: fmt(dspSavings, "currency"), sub: "saved annually", detail: "30% lower CPM via direct DSP — no LiveRamp" },
              { icon: Tv,         color: "bg-navy/10 text-navy",  label: "CTV",     stat: "17.4B", sub: "linked devices",  detail: "Person-level targeting on the biggest screen" },
              { icon: Smartphone, color: "bg-cyan/10 text-cyan",  label: "Mobile",  stat: "5.88B", sub: "mobile IDs",      detail: "One frequency cap across all devices" },
            ].map((ch, i) => (
              <div key={i} className="card p-3 text-center">
                <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2", ch.color)}>
                  <ch.icon size={15} />
                </div>
                <p className="text-xs font-bold text-navy">{ch.label}</p>
                <p className="text-lg font-black text-navy tabular mt-0.5">{ch.stat}</p>
                <p className="text-2xs text-slate">{ch.sub}</p>
                <p className="text-2xs text-slate/70 mt-1 leading-relaxed">{ch.detail}</p>
              </div>
            ))}
          </div>

          {/* 12-month projection */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-1">12-Month Cumulative Savings Projection</h3>
            <p className="text-xs text-slate mb-3">Includes data compounding effect as MRN match rates and suppression improve over time</p>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={projectionData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="savGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3056D3" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3056D3" stopOpacity={0}    />
                  </linearGradient>
                  <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#0891B2" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0891B2" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                  formatter={(v: number, n: string) => [fmt(v, "currency"), n]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="cumSavings"  stroke="#3056D3" strokeWidth={2} fill="url(#savGrad)"  name="Base Savings"       />
                <Area type="monotone" dataKey="compounding" stroke="#0891B2" strokeWidth={2} fill="url(#compGrad)" name="With Data Compounding" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Summary bar */}
          <div className="bg-navy rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-white/60 text-xs mb-1">12-Month MRN Impact Summary</p>
              <p className="text-white text-sm">
                Over 12 months, MRN delivers{" "}
                <span className="text-emerald-400 font-bold">{fmt(totalSavings, "currency")} in efficiency gains</span>
                {" "}and{" "}
                <span className="text-cyan font-bold">{fmt(newRevenueOppty * 0.15, "currency")} in new revenue</span>
                {" "}— a{" "}
                <span className="text-white font-black text-lg">{(animROI / 10).toFixed(1)}x</span>
                {" "}return on your MRN investment.
              </p>
            </div>
            <button className="btn-accent btn-sm flex-shrink-0 ml-4 flex items-center gap-2">
              Get Full Proposal <ArrowRight size={13} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}